import { EvaluationStatus } from '@/pipeline/stateMachine';
import {
  AdmissionType,
  EvaluationEnem,
  EvaluationSecondGraduation,
  EvaluationTransferGraduation,
} from '../modules/admission/state';
import { Course } from '../modules/course/state';
import { EssayTheme, EvaluationEssay } from '../modules/essay/state';
import { Campus, City } from '../modules/location/state';
import { UserInfo } from '../modules/user/state';
import { GetAdmission } from './queries/westerosQueries';
import { ONLY_LETTERS } from '@/helpers/string';

export interface NormalizedAdmission {
  admissionId?: number;
  evaluationStatus?: EvaluationStatus;
  user?: UserInfo;
  course?: Course;
  city?: City;
  campus?: Campus;
  admissionType?: AdmissionType;
  essay?: EvaluationEssay;
  essayTheme?: EssayTheme;
  enem?: EvaluationEnem;
  secondGraduation?: EvaluationSecondGraduation;
  transfer?: EvaluationTransferGraduation;
}

export function normalizeAdmission(admissionResult: GetAdmission | undefined): NormalizedAdmission {
  const admissionId: number | undefined = admissionResult?.id;

  const user: UserInfo = {
    id: admissionResult?.user.id,
    name: admissionResult?.user.fullName?.replace(ONLY_LETTERS, ''),
    email: admissionResult?.user.email,
    phone: admissionResult?.user.phone,
    signupMethod: admissionResult?.user.signupMethod,
  };

  const course: Course | undefined = admissionResult?.course;
  const admissionType: AdmissionType | undefined = admissionResult?.admissionType
    ? {
        id: admissionResult?.admissionType.id,
        name: admissionResult?.admissionType.name,
        description: admissionResult?.admissionType.description,
        stateKey: admissionResult?.admissionType.evaluationMethod,
      }
    : undefined;
  const campus: Campus | undefined = admissionResult?.campus;
  const city: City = { city: campus?.city, state: campus?.state };
  const essay: EvaluationEssay | undefined = admissionResult?.evaluationEssay
    ? {
        id: admissionResult?.evaluationEssay.id,
        title: admissionResult?.evaluationEssay.userEssayTitle,
        text: admissionResult?.evaluationEssay.userEssayText,
        hasAcceptedToS: admissionResult?.evaluationEssay.hasAcceptedToS,
        grade: admissionResult?.evaluationEssay.grade,
        document: admissionResult?.evaluationEssay.documentUrl
          ? { dataUrl: admissionResult?.evaluationEssay.documentUrl }
          : undefined,
      }
    : undefined;
  const essayTheme: EssayTheme | undefined = admissionResult?.evaluationEssay?.essayTheme;
  const enem: EvaluationEnem | undefined = admissionResult?.evaluationEnem
    ? {
        id: admissionResult?.evaluationEnem.id,
        year: String(admissionResult?.evaluationEnem.year),
        grades: {
          languages: admissionResult?.evaluationEnem.languagesGrade,
          humanScience: admissionResult?.evaluationEnem.humanScienceGrade,
          natureScience: admissionResult?.evaluationEnem.natureScienceGrade,
          math: admissionResult?.evaluationEnem.mathGrade,
          essay: admissionResult?.evaluationEnem.essayGrade,
        },
        hasAcceptedToS: admissionResult?.evaluationEnem.hasAcceptedToS,
        subscriptionNumber: admissionResult?.evaluationEnem.inscription,
      }
    : undefined;
  const secondGraduation:
    | EvaluationSecondGraduation
    | undefined = admissionResult?.evaluationSecondGraduation
    ? {
        id: admissionResult?.evaluationSecondGraduation.id,
        courseName: admissionResult?.evaluationSecondGraduation.courseName,
        institution: admissionResult?.evaluationSecondGraduation.institution,
        year: admissionResult?.evaluationSecondGraduation.year,
        hasAcceptedToS: admissionResult?.evaluationSecondGraduation.hasAcceptedToS,
        document: admissionResult?.evaluationSecondGraduation.documentUrl
          ? { dataUrl: admissionResult?.evaluationSecondGraduation.documentUrl }
          : undefined,
        documentBack: admissionResult?.evaluationSecondGraduation.documentBackUrl
          ? { dataUrl: admissionResult?.evaluationSecondGraduation.documentBackUrl }
          : undefined,
      }
    : undefined;

  const transfer: EvaluationTransferGraduation | undefined = admissionResult?.evaluationTransfer
    ? {
        id: admissionResult?.evaluationTransfer.id,
        courseName: admissionResult?.evaluationTransfer.courseName,
        institution: admissionResult?.evaluationTransfer.institution,
        periodYear: admissionResult?.evaluationTransfer.period,
        hasAcceptedToS: admissionResult?.evaluationTransfer.hasAcceptedToS,
        document: admissionResult?.evaluationTransfer.documentUrl
          ? { dataUrl: admissionResult?.evaluationTransfer.documentUrl }
          : undefined,
      }
    : undefined;

  const evaluationStatus: EvaluationStatus | undefined = admissionResult?.evaluationStatus;
  return {
    admissionId,
    evaluationStatus,
    user,
    course,
    city,
    campus,
    admissionType,
    essay,
    essayTheme,
    enem,
    secondGraduation,
    transfer,
  };
}
